// @flow
import React, { memo, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// $FlowFixMe styled-components beta
import { ThemeProvider as StyledThemeProvider, StyleSheetManager } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { equals } from 'ramda'
import { isTruthy } from 'ramda-adjunct'

import { init, LastFocusProvider, ReactWindowProvider } from '@alphaott/smart-tv-spatial-navigation'
import { ThemeProvider } from '@alphaott/smart-tv-themes'
import { NetworkErrorPopup, PageLoader } from '@alphaott/smart-tv-components'
import { AppAnalytics } from '@alphaott/app-analytics'
import { initMain, setBrandConfig } from '@alphaott/app-main/actions'
import stylisRTL from '@alphaott/styled-rtl'
import { useInitPlatform } from '@alphaott/smart-tv-platforms'

import { useNetworkStatus } from '@alphaott/smart-tv-current-device/hooks'

import { RootNavigation } from '../../navigations'
import { GlobalStyle } from '../../globalStyles'
import { ExitModalContainer } from '../ExitModalContainer'
import { withI18Next } from '../../hocs'
import { getBrandTheme } from '../../core/brandTheme'

type Store = any

init({
  throttle: 600,
})

/* eslint-disable complexity */
export const RootContainerInner = withI18Next(({ basename, history, theme }: any) => {
  const { i18n } = useTranslation()
  const { isDisconnectedNetwork } = useNetworkStatus()

  // TODO: Refactor getStylisPlugins, dir
  const isRtl = equals('rtl')
  const getStylisPlugins = (dir: 'rtl' | 'ltr') => (isRtl(dir) ? [stylisRTL] : [])

  const dir: 'ltr' | 'rtl' = i18n.dir() || 'ltr'
  const stylisPlugins = getStylisPlugins(dir)

  const isLoading = useSelector(
    (store: Store) => isTruthy(store.config.isLoading) || isTruthy(store.init.isLoading),
  )

  return (
    <StyleSheetManager stylisPlugins={stylisPlugins}>
      <ThemeProvider.Provider value={theme}>
        <ThemeProvider.Consumer>
          {({ styledTheme }) => (
            <StyledThemeProvider theme={{ ...styledTheme, dir }}>
              <GlobalStyle />
              <ExitModalContainer basename={basename}>
                <ReactWindowProvider>
                  <LastFocusProvider>
                    <PageLoader isLoading={isLoading}>
                      <AppAnalytics source="smarttv">
                        <RootNavigation basename={basename} history={history} />

                        {isDisconnectedNetwork && <NetworkErrorPopup />}
                      </AppAnalytics>
                    </PageLoader>
                  </LastFocusProvider>
                </ReactWindowProvider>
              </ExitModalContainer>
            </StyledThemeProvider>
          )}
        </ThemeProvider.Consumer>
      </ThemeProvider.Provider>
    </StyleSheetManager>
  )
})

export const RootContainer = ({ basename, history, children }: any) => {
  const dispatch = useDispatch()

  const theme = useMemo(() => getBrandTheme(), [])
  const isAppInitSuccess = useSelector((store: Store) => store.init.isSuccess)

  useInitPlatform()

  useEffect(() => {
    if (!isAppInitSuccess) {
      dispatch(setBrandConfig(theme.brandConfig))
      dispatch(initMain({ fetchBrandConfig: false }))
    }
  }, [dispatch, isAppInitSuccess]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <RootContainerInner basename={basename} history={history} theme={theme}>
      {children}
    </RootContainerInner>
  )
}

export default memo(RootContainer)
